<template>
	<div class="upload-wrapper dont-close-side-modals" :class="{'error-state': errorState}">
		<!-- label -->
		<label-generic
		v-if="label"
		for="customDropzone"
		class="component-label"
		:text="label"
		:mandatory="mandatory"
		:errorState="errorState"/>
		<!-- Fake Success -->
		<fake-success
		v-if="showSuccessFeedback"
		id="fake-success-dropzone"
        :sucessMessage="t('fileUploader.defaultSuccessMessage')"
		:fileName="initialFile.name"
		:uploaderIdentifier="uploaderIdentifier"
		:uploadingMessage="defaultUploadingMessage">
            <dropdown-more-options
            :options="fakeSuccessMoreOptionsParams"
            @clicked="fakeSuccessMoreOptionsParams.find(obj => obj.id === $event).callbackFunction()"/>
        </fake-success>
        <!-- Dropzone -->
		<vue-dropzone
		ref="myDropzone"
		id="custom-dropzone"
		:options="dropOptions"
		:useCustomSlot="true"
        :class="{'with-fake-success-dropzone': showSuccessFeedback}"
		@vdropzone-success="handleSuccessUpload"
		@vdropzone-error="handleErrorUpload"
		@vdropzone-file-added="fileAdded"
		@vdropzone-file-added-manually="retryRequest"
		@vdropzone-thumbnail="thumbnailCreated"
		@vdropzone-drop="handleDrop">
			<div class="uploader-face-layout dont-close-side-modals" :id="`uploader-face-layout${props.uploaderIdentifier}`">
				<attach-icon/>
				<p class="no-hover">{{defaultPlaceholder}}</p>
				<p class="hover">{{defaultDragPlaceholder}}</p>
			</div>
		</vue-dropzone>
		<dropdown-more-options
		v-if="showMoreOptions"
		:options="moreOptionsParams"
		@clicked="moreOptionsParams.find(obj => obj.id === $event).callbackFunction()"/>
        <!-- upload size note -->
        <div v-if="!errorState && !hideSizeNote" class="size-note" v-html="defaultFileNote"/>
		<!-- ErrorState Message -->
        <div v-if="errorState" class="error-state-message">{{errorMessage}}</div>
        <!-- Slot content -->
        <slot></slot>
	</div>
</template>

<script setup>
/* eslint-disable */
/************************************
* @PROPS						    *
* label								*
* createThumbnail					*
* placeholder 						*
* uploadingMessage 					*
* successMessage 					*
* errorMessage						*
* autoUpload						*
* url								*
* maxFilesize						*
* uploadIdentifier					*
* replaceUrl						*
* putOnReplace						*
* initialFile						*
* 									*
* @EMITTED EVENTS					*
* file-added (file)					*
* file-removed ()					*
* before-file-replaced ()			*
* upload-canceled ()				*
* upload-success (file, response)	*
* upload-error (file, error, xhr)	*
*************************************
/* eslint-enable */
import LabelGeneric from '@/components/generic/labels/Generic'
import VueDropzone from '@/components/generic/uploader/dropzone/VueDropzone'
import FakeSuccess from '@/components/generic/uploader/dropzone/FakeSuccess'
import AttachIcon from '@/assets/icons/attach.svg?inline'
import DropdownMoreOptions from '@/components/generic/dropdowns/moreOptions/MoreOptions'
import '@/assets/dropzone/vueDropzone.min.css'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
const { getAppThemeMode } = mapGetters()

/* Vue */
import { ref, computed, watch, onMounted, onBeforeMount } from 'vue'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits([
    'requestExtraInfoBeforeUpload',
    'upload-success',
    'file-added',
	'file-removed',
    'before-file-replaced',
    'upload-canceled',
    'uploadSuccessWithResponse',
    'upload-error',
    'removeShowSuccessFeedback'
])

const props = defineProps({
	errorMessage: String,
	errorState: Boolean,
	mandatory: Boolean,
    label: String,
    placeholder: String,
    dragPlaceholder: String,
    uploadingMessage: String,
    successMessage: String,
    errorMessage: String,
    hideSizeNote: Boolean,
    fileNote: String,
    autoUpload: {
        type: Boolean,
        default: true
    },
    /* Sometimes we need to do something before starting our upload */
    needsExtraInfoBeforeUpload: Boolean,
    imageContainerId: {
        type: String,
        default: ''
    },
    /* used to identify the uploader when there is more than 1 at the same time */
    uploaderIdentifier: {
        type: String,
        default: ''
    },
    url: {
        type: String,
        default: 'teste' /* We cant have a emptyUrl so we send this by default */
    },
    replaceUrl: String, // if we need a specific url to be used on replace
    putOnReplace: Boolean, // if we need the method to change to put on replace
    maxFilesize: [String, Number],
    initialFile: [Object, File], // if we need to receive a file to be added on begin
    removeFile: Boolean, // when we clean our step and want to force cleaning our file
    /* if we want to display visually like we had already uploaded previously a file but we don't have it */
    showSuccessFeedback: Boolean,
    fileName: String
})

const dropOptions = ref({
    createImageThumbnails: props.imageContainerId.length > 0,
    previewTemplate: '',
    autoProcessQueue: false,
    maxFiles: 1,
    url: props.url,
    maxFilesize: props.maxFilesize ? props.maxFilesize : 4, // default size
    maxThumbnailFilesize: props.maxFilesize ? props.maxFilesize : 4 // default size
})

const lastFileAdded = ref(null)
const lastFileUrl = ref(null)

const requestAfterInfoFile = ref(false)

const showActionTooltip = ref(false)

const myDropzone = ref('')

const defaultFileNote = computed(() => {
    return props.fileNote ? props.fileNote : t('fileUploader.fileNote')
})

const defaultPlaceholder = computed(() => {
    return props.placeholder ? props.placeholder : t('fileUploader.defaultPlaceholder')
})

const defaultDragPlaceholder = computed(() => {
    return props.dragPlaceholder ? props.dragPlaceholder : t('fileUploader.defaultDragPlaceholder')
})

const defaultUploadingMessage = computed(() => {
    return props.uploadingMessage ? props.uploadingMessage : t('fileUploader.defaultUploadingMessage')
})

const defaultSuccessMessage = computed(() => {
    return props.successMessage ? props.successMessage : t('fileUploader.defaultSuccessMessage')
})

const defaultErrorMessage = computed(() => {
    return props.errorMessage ? props.errorMessage : t('fileUploader.defaultErrorMessage')
})
 
/** @function
* thumbail created
*/
const thumbnailCreated = (file, dataUrl) => {
    createImageThumbnail(file, dataUrl)
}


/** @function
*  event callback for when a file is added
*/
const fileAdded = file => {
    if (props.showSuccessFeedback) emit('removeShowSuccessFeedback')

    Array.from(document.querySelectorAll('.uploader-icon')).forEach(
        (el) => {
            if (getAppThemeMode.value === 'dark') el.classList.add('dark')
            else el.classList.remove('dark')
        }
    )

	if (lastFileAdded.value !== null) {
    	myDropzone.value.removeFile(lastFileAdded.value) 
    }

	showMoreOptions.value = true
    /**
    * we have a setTimeout here, to make sure
    * we only fire the processQueue when the
    * is already there
    */
    setTimeout(() => {
        /**
        * we save the file added
        * so later on in the case that the upload fails
        * we are going to use this file to "retry" ,
        * so we are not forcing the user to choose again
        */
        lastFileAdded.value = file
        /**
        * here we are manually processing the queue ,
        * it is here that the requests are really fired to the server
        * in case of need , this can be called later on ,
        * if we dont want the request to be fired right after the
        * image being selected
        */
        if (props.needsExtraInfoBeforeUpload) {
            emit('requestExtraInfoBeforeUpload')
            requestAfterInfoFile.value = true
            manuallySetLoading()
        } else if (props.autoUpload) {
            myDropzone.value.processQueue()
        } else if (file.status !== 'error') {
            manuallySetSuccess()
            emit('upload-success', file)
			emit('file-added', file)
        }
    }, 0)

    setupEventListeners()
}

/** @function
* callback associated with the action tooltip
* to replace the selected file
*/
const replaceFile = noClickEvent =>{
    showActionTooltip.value = false
    emit('before-file-replaced')

	/* if we get a replace url , we need to change the url to this one */
    if (props.replaceUrl) {
        myDropzone.value.dropzone.options.url = props.replaceUrl
        myDropzone.value.options.url = props.replaceUrl
    }
	/* if the service to replace is a put we need to change it */
    if (props.putOnReplace) {
        myDropzone.value.dropzone.options.method = 'put'
        myDropzone.value.options.method = 'put'
    }

    if (props.imageContainerId) {
        document.getElementById(props.imageContainerId).style.backgroundImage = 'none'
    }

    if (!noClickEvent) {
		document.getElementById(`uploader-face-layout${props.uploaderIdentifier}`).click()
	}
}

const handleDrop = () => {
    replaceFile('noClickEvent')
}

/** @function
* callback associated with the action tooltip
* to replace the selected file
*/
const deleteFile = () => {
    showActionTooltip.value = false
	showMoreOptions.value = false
    myDropzone.value.removeAllFiles(true)

    if(props.imageContainerId) {
        document.getElementById(props.imageContainerId).style.backgroundImage = 'none'
    }

    emit('file-removed')
}

/** @function
* cancel the upload of a file currently in progress
*/
const cancelUpload = () => {
    myDropzone.value.removeAllFiles(true)
    emit('upload-canceled')
}

/** @function
* event listeners for the option buttons
* that appear on the feedback page
* we bind them here, because it is the first step
* where we have sure they are already in the dom and
* ready to receive the events
*/
const setupEventListeners = () => {
    /**
    * first we remove the old events, to avoid
    * double binding
    */
    document.getElementById(`more-options${props.uploaderIdentifier}`).removeEventListener('click', actionTooltipHandler, true)
    document.getElementById(`cancel-upload${props.uploaderIdentifier}`).removeEventListener('click', cancelUpload, true)
    /**
    * then we create the new ones
    */
    document.getElementById(`more-options${props.uploaderIdentifier}`).addEventListener('click', actionTooltipHandler)
    document.getElementById(`cancel-upload${props.uploaderIdentifier}`).addEventListener('click', cancelUpload)
}

/** @function
* adds and removes events
* to the document, to allow the user
* to close the tooltip whenever he clicks outside of it
*/
const actionTooltipHandler = ev => {
    if (ev.target.parentNode.id !== 'dz-details') {
        showActionTooltip.value = false
    } else {
        showActionTooltip.value = !showActionTooltip.value
    }
    if (showActionTooltip.value) {
        document.addEventListener('click', actionTooltipHandler)
    } else {
        document.removeEventListener('click', actionTooltipHandler)
    }
    ev.stopPropagation()
}

/** @function
* this will trigger when a file is manually added
* we will use this in the case that the upload is only triggered
* later and not when the image is selected , so if we need to retry a request
* we will probably need this function
*/
const retryRequest = ()=> {
    // TODO
}

/** @function
* triggers on upload success
*/
const handleSuccessUpload = (file, response) => {
    document.getElementById(`custom-detail-message${props.uploaderIdentifier}`).innerHTML = defaultSuccessMessage.value
    emit('upload-success', file, response)
    emit('uploadSuccessWithResponse', {file: file, response: response})
}

/** @function
* triggers on upload error
*/
const handleErrorUpload = (file, message, xhr) => {
	if(file.status !== 'canceled') {
		document.getElementById(`custom-detail-message${props.uploaderIdentifier}`).innerHTML = defaultErrorMessage.value
        document.getElementById(`uploaded-filename${props.uploaderIdentifier}`).innerHTML = t('fileUploader.tryAgain')
        emit('upload-error', file, message, xhr)
    }
}

/** @function
* manually add a file to the uploader
*/
const manuallyAddFile = (file, dataUrl) => {
    lastFileAdded.value = file
    lastFileUrl.value = dataUrl
    myDropzone.value.manuallyAddFile(file, dataUrl)
}

/** @function
* manually sets the success screen
*/
const manuallySetSuccess = () => {
    document.getElementById(`dz-preview${props.uploaderIdentifier}`).parentNode.classList.add('dz-started')
    document.getElementById(`dz-preview${props.uploaderIdentifier}`).classList.add('dz-success')
    document.getElementById(`dz-preview${props.uploaderIdentifier}`).classList.add('dz-complete')
    document.getElementById(`custom-detail-message${props.uploaderIdentifier}`).innerHTML = defaultSuccessMessage.value
}

/** @function
* manually sets the success screen
*/
const manuallySetLoading = () => {
    document.getElementById(`dz-preview${props.uploaderIdentifier}`).classList.add('dz-processing')
}

/** @function
* The interface for everything that happens
* after the user select an image
*/
const template = () => {
    return `<div id="dz-preview${props.uploaderIdentifier}" class="dz-preview dz-file-preview">
        <div class="dz-image">
            <img data-dz-thumbnail>
        </div>
        <div id="dz-details" class="dz-details">
            <div class="dz-size"><span data-dz-size></span></div>
            <div class="icon-status ${getAppThemeMode.value} uploader-icon"></div>
            <div id="custom-detail-message${props.uploaderIdentifier}" class="custom-detail-message"></div>
            <div id="uploaded-filename${props.uploaderIdentifier}" class="dz-filename"><span data-dz-name></span></div>
            <div id="try-again${props.uploaderIdentifier}" class="icon-try-again"></div>
            <div id="more-options${props.uploaderIdentifier}" class="icon-more-options"></div>
            <div class="icon-upload-in-progress"></div>
            <div id="cancel-upload${props.uploaderIdentifier}" class="icon-cancel-upload"></div>
            <div class="uploading-text">${defaultUploadingMessage.value}</div>
        </div>
        <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
        <div class="dz-error-message"><span data-dz-errormessage></span></div>
        <div class="dz-success-mark"><i class="fa fa-check"></i></div>
        <div class="dz-error-mark"><i class="fa fa-close"></i></div>
    </div>`
}

const createImageThumbnail = (file, dataUrl) => {
    let thumbnailElement = document.getElementById(props.imageContainerId)
    thumbnailElement.alt = file.name
    thumbnailElement.style.backgroundImage = `url(${dataUrl})`
}

const showMoreOptions = ref(false)

const fakeSuccessMoreOptionsParams = [
	{
		id: 1,
		text: t('fileUploader.replace'),
		icon: 'cycle',
		callbackFunction: () => {
            document.getElementById(`uploader-face-layout${props.uploaderIdentifier}`).click()
        }
	}, 
	{
		id: 2,
		text: t('fileUploader.delete'),
		icon: 'delete',
		variant: 'negative',
		callbackFunction: () => { emit('removeShowSuccessFeedback') }
	}
]

const moreOptionsParams = [
	{
		id: 1,
		text: t('fileUploader.replace'),
		icon: 'cycle',
		callbackFunction: () => { replaceFile() }
	}, 
	{
		id: 2,
		text: t('fileUploader.delete'),
		icon: 'delete',
		variant: 'negative',
		callbackFunction: () => { deleteFile() }
	}
]

watch(() => props.replaceUrl, newVal => {
	props.replaceUrl = newVal
})

watch(() => props.removeFile, newVal => {
	if (newVal) deleteFile()
})

watch(() => getAppThemeMode.value, newVal => {
    Array.from(document.querySelectorAll('.uploader-icon')).forEach(
        (el) => {
            if (newVal === 'dark') el.classList.add('dark')
            else el.classList.remove('dark')
        }
    )
})

watch(() => props.needsExtraInfoBeforeUpload, () => {
	myDropzone.value.dropzone.options.url = props.url
    myDropzone.value.options.url = props.url

    if (requestAfterInfoFile.value) {
        requestAfterInfoFile.value = false
        myDropzone.value.dropzone.processQueue()
    }
})

onBeforeMount(() => {
	dropOptions.value.previewTemplate = template()
 })

onMounted(() => {
    /**
    * if there is an initial file, we need to
    * manually add it , then we call the manually set success
    * cause in this case we use this to go back in the process
    * and show the success to the person, show she doesnt think
    * that needs to upload de file again, if in the future ,
    * we want to pass a file to be added manually , but dont
    * want to show the success , we need to add a flag alongside
    * with the file, but for now it will stay like this, cause
    * there is no pratical example that shows us we need to deal
    * with the other version of the problem
    */
    if(props.initialFile) {
        if (props.initialFile.status === 'canceled') return
        manuallyAddFile(props.initialFile, '')
        setTimeout(() => {
            manuallySetSuccess()
        },100)
    }
})
</script>

<style lang="scss" scoped>
.upload-wrapper { // the wrapper where the upload is rendered
	width:100%;
    max-width: 508px;

	display:flex;
	flex-direction:column;
	position:relative;

	label.component-label { // label that appears above the uploader area
		height: 16px;
		margin-bottom: 0;
		margin-bottom: 8px;
        
		display: block;

		@include H7-Regular;
		color: var(--secondary-dark-1);
	}

	#custom-dropzone { // dropzone wrapper
		max-width: 508px;
		min-height: 56px;
    	height: 56px;
		padding: 0;

		line-height: 56px;
		border: none;
		border-radius: 8px;
		background-color: var(--secondary-light-2);

        &.with-fake-success-dropzone {
            visibility: hidden;
            height: 0;
            min-height: 0;
        }

		:deep(.dz-message) { 
            margin: 0;
         } // what you see before any file being uploaded

		.uploader-face-layout { // custom module used in a slot to display this content before any file being uploaded
			display:flex;
			flex-direction:row;
			align-items:center;
			justify-content:flex-start;

			svg {
				margin-left: 16px;

                :deep(.change-color-fill) {
                    fill: var(--secondary);
                }
			}

			p {
				padding: 0;
				margin: 0;
				margin-left: 8px;

				@include H7-Medium;
				letter-spacing: normal;
				color: var(--secondary-dark-2);
			}
		}

		:deep(.dz-details) { // what you see after a file being uploaded
			width: 100%;
			height: 100%;
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			justify-content: center;
			padding: 0;
			padding-left: 48px;
			border-radius: 8px;
			opacity: 1;

			.dz-size { display: none; } // displays the size of the file

			.icon-status { // displays the icon for success or error
				width: 20px;
				height: 20px;
				display: none;
				position: absolute;
				left: 16px;
				top: 16px;
				background-size: cover;
			}

			.icon-upload-in-progress { // displays the upload icon
				display: none;
				position: absolute;
				left: 16px;
				top: 16px;
				width: 24px;
				height: 24px;
				background-image: url('~@/assets/dropzone/icons/upload.svg');
			}

			.icon-cancel-upload {
				display: none;
				position: absolute;
				right: 16px;
				top: 16px;
				width: 24px;
				height: 24px;
				cursor: pointer;
				background-image: url('~@/assets/dropzone/icons/clear-back.svg');
			}

			.custom-detail-message { // displays the message about the upload being a success or not
				@include H7-Medium;
				color: var(--secondary-light-4);
			}

			.dz-filename { // displays the name of the file
				max-width: calc(100% - 50px);
				padding: 0;

				@include H8-Regular;
				color: var(--secondary-light-4);

				span { 
                    padding: 0; 
                }
			}

			.uploading-text {
				display: none;
				height: 14px;
				line-height: 14px;
				opacity: 0.72;
				@include H7-Medium;
				color: var(--secondary-light-4);
				margin-left: 10px;
				margin-top: 20px;
			}
		}

		:deep(.dz-processing) { //the component during the process of uploading
			.dz-details { background-color: var(--white); }
			.dz-filename { display: none; }
			.uploading-text { display: block; }
			.icon-upload-in-progress { display: block; }
			.icon-cancel-upload { display: block; }
		}

		:deep(.dz-progress) { // the progress bar
			width: calc(100% - 120px);
			height: 4px;
			background: #e4ebee;
			position: absolute;
			left: 60px;
			top: 18px;
			margin: 0;
			margin-bottom: 10px;
			.dz-upload {
				background-color:var(--secondary-variant-5);
			}
		}

		:deep(.dz-success) { // class applied when the file upload was a success
			.dz-progress { display: none }
			.dz-details {
				background-color:var(--positive);
				box-shadow: 0 1px 7px 0 rgba(1, 165, 129, 0.32);
				border-radius: 8px;

				.icon-status {
					display: block;
					background-image: url('~@/assets/dropzone/icons/success.svg');
                    opacity: 0.56;

                    &.dark {
                        background-image: url('~@/assets/dropzone/icons/success-dark.svg');
                    }
				}
			}
			.uploading-text { display: none; }
			.icon-upload-in-progress { display: none; }
			.icon-cancel-upload { display: none; }
			.dz-filename { display: block; }
			.icon-more-options { display: block; }
		}
		:deep(.dz-error) { // class applied when the file upload was a fail
			.dz-progress { display: none }
			.dz-details {
				background-color: var(--negative);
				box-shadow: 0 1px 7px 0 rgba(208, 46, 68, 0.32);
				border-radius: 8px;
				.icon-status {
					display: block;
					background-image: url('~@/assets/dropzone/icons/error.svg');
				}
			}
			.uploading-text { display: none; }
			.icon-upload-in-progress { display: none; }
			.icon-cancel-upload { display: none; }
			.dz-filename {
				display: block;
				color: #f1c4ca;
			}
			.icon-more-options { display: block; }
		}

		:deep(.dz-preview) {
			width: 100%;
			min-height: 56px;
			height: 56px;
			margin: 0;

			&:hover {
				z-index: 98;
			}
		}
		:deep(.dz-success-mark) { display: none; } //icon that pops up when a file is successfully uploaded
		:deep(.dz-error-mark) { display: none; } //icon that pops up when a file doesnt upload with success
		:deep(.dz-image) { display: none; } // the thumbnail image of the file uploaded
		:deep(.dz-error-message) { display: none; } // the overlay with the message from the server

		:deep(.hover) {
			width: 100%;
			margin: 0;
    		justify-content: center;
			display: none;
			pointer-events: none;
			color: var(--primary-variant-2);
		}

		svg, .no-hover {
			pointer-events: none;
		}

		&.dz-drag-hover {
			.dz-message {
				opacity: 1;
			}
			box-shadow: unset;
			border: dashed 2px var(--secondary-variant-6);
			color: var(--primary-variant-2);

			svg, .no-hover { display: none;}
			.hover {
                display: flex; 
                color: var(--primary-variant-2); 
            }
		}
	}
	:deep(.dropdown-wrapper) {
		position: absolute;
		top: 32px; //the label + 10px inside the component
		right: 8px;
		z-index: 99; //we need this value to be bigger than the dropzone while on hover

		.button-quaternary {
			background-color: var(--secondary-light-4);
		}
	}

    .size-note {
        max-width: 400px;
		margin-top: 8px;

		@include H7-Regular;
		color: var(--secondary-dark-1);
		line-height: 16px;
    }

	.error-state-message {
		max-width: 400px;
		margin-top: 8px;

		@include H7-Regular;
		color: var(--negative);
	}

	&.error-state {
		label.component-label { // label that appears above the uploader area
			color: var(--negative);
		}

		#custom-dropzone, #fake-success-dropzone { 
			border: solid 1px var(--negative);

			.uploader-face-layout { // custom module used in a slot to display this content before any file being uploaded
				svg {
					:deep(.change-color-fill) {
						fill: var(--negative);
					}
				}
				
				p {
					color: var(--negative);
				}
			}
		}


	}
}
</style>